import { createAuthClient } from 'better-auth/vue'
import { anonymousClient, emailOTPClient } from 'better-auth/client/plugins'
import {
    appendDesignerCompatibilityRequestHeaders,
    tryApplyClientIncompatibleFromResponse,
} from '~~/layers/base/utils/designerMinAppVersionPolicy'
import { isDeviceOffline } from '~~/layers/base/utils/isDeviceOffline'
import {
    getPreservedBetterAuthGetSessionStubResponse,
    isBetterAuthGetSessionRequest,
} from '~~/layers/base/utils/ecommerceDesignerBetterAuthInjunction'
import { isEcommerceDesignerPath } from '~~/layers/base/utils/isEcommerceDesignerRoute'

type NuxtPublicConfig = {
    betterAuthUrl?: string
}

const CAPACITOR_BEARER_PREF_KEY = 'better_auth_session_bearer'
const CAPACITOR_BEARER_LOCAL_FALLBACK_KEY = 'better_auth_session_bearer_local'

/**
 * Capacitor bundled WebView origins (not live-reload / browser tabs):
 * - iOS: `capacitor://localhost`
 * - Android: `https://localhost` (default `androidScheme: https`) — still `http(s):`, so a
 *   protocol-only check wrongly treats it as same-origin browser auth → `/api/auth` on localhost.
 * Dev browser / `CAPACITOR_LIVE_RELOAD` use `http://local.crane.com:3000` or a LAN host:port and
 * must keep same-origin auth.
 */
function isCapacitorBundledWebViewOrigin(): boolean {
    if (typeof window === 'undefined') {
        return false
    }
    const { protocol, hostname, port } = window.location
    if (protocol === 'capacitor:') {
        return true
    }
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
        return false
    }
    // Bundled Android WebView has no explicit port (https://localhost/). `:3000` is local Nuxt.
    if (protocol === 'https:') {
        return port === '' || port === '443'
    }
    if (protocol === 'http:') {
        return port === '' || port === '80'
    }
    return false
}

function getCapacitorSafeAuthBaseURL(): string | undefined {
    if (typeof window === 'undefined' || !isCapacitorBundledWebViewOrigin()) {
        return undefined
    }

    const publicConfig = (
        window as Window & { __NUXT__?: { config?: { public?: NuxtPublicConfig } } }
    ).__NUXT__?.config?.public
    const configuredBase = publicConfig?.betterAuthUrl?.trim()
    if (!configuredBase) {
        return undefined
    }

    // Better Auth expects an absolute http(s) URL and defaults endpoints under /api/auth.
    return `${configuredBase.replace(/\/+$/, '')}/api/auth`
}

/** True in the Capacitor shell (iOS custom scheme or Android https://localhost), not a browser tab. */
function isNativeCapacitorShell(): boolean {
    if (typeof window === 'undefined') {
        return false
    }
    if (isCapacitorBundledWebViewOrigin()) {
        return true
    }
    const protocol = window.location.protocol
    return protocol !== 'http:' && protocol !== 'https:'
}

async function getCapacitorBearerToken(): Promise<string | null> {
    if (!isNativeCapacitorShell()) {
        return null
    }
    // Prefer sync localStorage first: Preferences is async and sign-in's follow-up
    // get-session can race Preferences.set.
    try {
        const local = window.localStorage.getItem(CAPACITOR_BEARER_LOCAL_FALLBACK_KEY)
        if (local) {
            return local
        }
    } catch {
        // Fall through to Preferences.
    }
    try {
        const { Preferences } = await import('@capacitor/preferences')
        const { value } = await Preferences.get({ key: CAPACITOR_BEARER_PREF_KEY })
        if (value) {
            return value
        }
    } catch {
        // No native Preferences.
    }
    return null
}

/**
 * Session bearer token stored for Capacitor (Preferences / localStorage fallback).
 * Used by Better Auth client fetch hooks and by `useApiFetch` — those must stay in sync.
 */
export async function getCapacitorSessionBearerToken(): Promise<string | null> {
    return getCapacitorBearerToken()
}

/**
 * Android CapacitorCookies already attaches the cross-origin Better Auth session cookie.
 * Stock `bearer()` verifies Authorization then `headers.append("cookie", …)`, which corrupts
 * Cookie parsing when both are present → `/get-session` returns null. Skip Bearer on Android
 * (Cookie only). iOS WKWebView often omits that cookie, so Bearer stays required there.
 */
async function shouldAttachCapacitorAuthorizationBearer(): Promise<boolean> {
    try {
        const { Capacitor } = await import('@capacitor/core')
        return Capacitor.getPlatform() !== 'android'
    } catch {
        return true
    }
}

/**
 * Attach Capacitor session `Authorization: Bearer` for cross-origin API/auth calls.
 * No-op on Android (Cookie-only); iOS still sends Bearer.
 */
export async function attachCapacitorSessionBearerAuth(
    headers: Headers,
    _targetBaseUrl?: string | null
): Promise<void> {
    if (!isNativeCapacitorShell()) {
        return
    }
    if (!(await shouldAttachCapacitorAuthorizationBearer())) {
        // Drop any prior Authorization so stock bearer() does not run.
        headers.delete('Authorization')
        return
    }
    const token = await getCapacitorBearerToken()
    if (!token) {
        return
    }
    headers.set('Authorization', `Bearer ${token}`)
}

/**
 * Explicitly persists a Capacitor session bearer token. Normally this happens automatically via
 * the `onResponse` hook below, which reads the `set-auth-token` response header. But that header
 * can be dropped by a cross-origin hop (Capacitor's `capacitor://localhost` calling a separate
 * HTTPS auth origin) if something in front of the API doesn't preserve
 * `Access-Control-Expose-Headers`, while the JSON response body is never subject to that
 * restriction. Callers that receive a rotated session `token` directly in a response body (e.g.
 * `change-password.vue` after `revokeOtherSessions: true`) should persist it explicitly with this
 * function rather than relying solely on the header round-trip.
 */
export async function setCapacitorSessionBearerToken(token: string | null): Promise<void> {
    return setCapacitorBearerToken(token)
}

function resolveRequestUrl(input: RequestInfo | URL): string {
    if (typeof input === 'string') {
        return input
    }
    if (input instanceof URL) {
        return input.href
    }
    return input.url
}

function isBetterAuthApiRequest(url: string): boolean {
    return url.includes('/api/auth')
}

/** TK-13530 temporary injunction — remove with `isEcommerceDesignerRoute` cutover. */
function shouldStubBetterAuthOnEcommerceDesigner(url: string): boolean {
    if (typeof window === 'undefined') {
        return false
    }
    if (!isEcommerceDesignerPath(window.location.pathname)) {
        return false
    }
    return isBetterAuthApiRequest(url)
}

function stubBetterAuthEmptySessionResponse(): Response {
    return new Response(JSON.stringify({ session: null, user: null }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
    })
}

async function setCapacitorBearerToken(token: string | null): Promise<void> {
    if (!isNativeCapacitorShell()) {
        return
    }
    // Sync localStorage before any await so the next get-session can read the bearer
    // immediately (atom refresh after sign-in/email races Preferences.set).
    try {
        if (token == null || token === '') {
            window.localStorage.removeItem(CAPACITOR_BEARER_LOCAL_FALLBACK_KEY)
        } else {
            window.localStorage.setItem(CAPACITOR_BEARER_LOCAL_FALLBACK_KEY, token)
        }
    } catch {
        // Ignore local fallback failures.
    }
    try {
        const { Preferences } = await import('@capacitor/preferences')
        if (token == null || token === '') {
            await Preferences.remove({ key: CAPACITOR_BEARER_PREF_KEY })
        } else {
            await Preferences.set({ key: CAPACITOR_BEARER_PREF_KEY, value: token })
        }
    } catch {
        // localStorage already updated above.
    }
}

/**
 * Persist a Capacitor bearer after sign-in/sign-up when `set-auth-token` was missing.
 *
 * Body `token` is the **raw** session id. The `set-auth-token` response header is the
 * **signed** cookie value (`token.signature`) — that is what Better Auth's bearer plugin
 * expects. Overwriting a signed value with the raw body token makes `/get-session` return
 * `null` when Bearer is used (iOS).
 */
export async function persistCapacitorBearerFromAuthData(
    data: { token?: string | null } | null | undefined
): Promise<void> {
    const token = data?.token?.trim()
    if (!token) {
        return
    }
    try {
        const existing = window.localStorage.getItem(CAPACITOR_BEARER_LOCAL_FALLBACK_KEY)
        // Keep signed `set-auth-token` if onResponse already stored it.
        if (existing?.includes('.') && !token.includes('.')) {
            return
        }
    } catch {
        // Fall through and store body token as last resort.
    }
    await setCapacitorSessionBearerToken(token)
}

/**
 * Better Auth client for the frontend. Uses relative URLs so requests go to
 * the same origin (default path /api/auth). For Vue/Nuxt use the composable
 * useBetterAuth() which wraps this client.
 *
 * **Capacitor:** WKWebView often does not persist cross-origin session cookies even with
 * `SameSite=None`. The server `bearer` plugin exposes `set-auth-token` on auth responses; we store
 * it in Capacitor Preferences and send `Authorization: Bearer` on subsequent requests (iOS).
 * Android uses CapacitorCookies instead and omits Bearer — see `attachCapacitorSessionBearerAuth`.
 */
export const authClient = createAuthClient({
    baseURL: getCapacitorSafeAuthBaseURL(),
    plugins: [anonymousClient(), emailOTPClient()],
    fetchOptions: {
        /**
         * Skip the real network when the device is already known offline. Throwing here settles
         * Better Auth's `useSession` pending state without a WebKit "Failed to load resource" line
         * for `/get-session` (and other auth calls).
         */
        customFetchImpl: async (input, init) => {
            const url = resolveRequestUrl(input)
            if (shouldStubBetterAuthOnEcommerceDesigner(url)) {
                if (isBetterAuthGetSessionRequest(url)) {
                    const preserved = getPreservedBetterAuthGetSessionStubResponse()
                    if (preserved) {
                        return preserved
                    }
                }
                return stubBetterAuthEmptySessionResponse()
            }
            if (await isDeviceOffline()) {
                throw new TypeError('Failed to fetch')
            }
            return globalThis.fetch(input, init)
        },
        async onRequest(context) {
            try {
                if (!isNativeCapacitorShell()) {
                    return context
                }
                const headers = new Headers(context.headers ?? undefined)
                const authBase = getCapacitorSafeAuthBaseURL()
                await appendDesignerCompatibilityRequestHeaders(headers, authBase)
                await attachCapacitorSessionBearerAuth(headers, authBase)
                return { ...context, headers }
            } catch {
                // Never block auth requests if token hydration/header mutation fails.
                return context
            }
        },
        async onResponse({ response }) {
            try {
                if (!isNativeCapacitorShell()) {
                    return
                }
                await tryApplyClientIncompatibleFromResponse(response)
                if (!response.ok) {
                    return
                }
                // Prefer signed cookie value from the bearer plugin (not JSON body `token`).
                const token = response.headers.get('set-auth-token')
                if (token) {
                    await setCapacitorBearerToken(token)
                }
            } catch {
                // Ignore persistence failures; request already completed.
            }
        },
        async onError(context) {
            try {
                if (!isNativeCapacitorShell()) {
                    return
                }
                const response = (context as { response?: Response }).response
                if (response) {
                    await tryApplyClientIncompatibleFromResponse(response)
                }
            } catch {
                // Ignore; auth error handling proceeds as usual.
            }
        },
        async onSuccess(context) {
            try {
                if (!isNativeCapacitorShell()) {
                    return
                }
                const url = String(context.request?.url ?? '')
                if (url.includes('/sign-out')) {
                    await setCapacitorBearerToken(null)
                }
            } catch {
                // Ignore cleanup failures.
            }
        },
    },
})
